import { Injectable } from '@angular/core';
import { map, catchError, switchMap, delay } from 'rxjs/operators';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { ToastrService } from 'ngx-toastr';
import { IAccountUser } from '@account-users/account/models/IAccountUser';
import {
  ILoadAccountUserPayload,
  IUpdateLicensePayload,
  IUpdateSectionPayload,
} from '@account-users/models/IAccountUser';
import {
  AccountUserActionTypes,
  LoadAccountUserPermissionsSuccess,
  LoadAccountUserPermissionsFailure,
  UpdateAccountUserLicensePermissionsSuccess,
  UpdateAccountUserLicensePermissionFailure,
  UpdateAccountUserSectionPermissionsSuccess,
  UpdateAccountUserSectionPermissionsFailure,
  LoadAccountUserSuccess,
  LoadAccountUserFailure,
  LoadAllAccountUserLicensesAction,
  LoadAllAccountUserLicensesActionSuccess,
  LoadAllAccountUserLicensesActionFailure,
  UpdateAccountUserRolesAction,
  UpdateAccountUserRolesActionSuccess,
  UpdateAccountUserRolesActionFailure,
  LoadAccountUserAfterRoleSuccess,
  LoadAccountUserAfterRoleFailure,
} from './account-user.actions';
import { AccountService } from '@account-users/account/services/account.service';
import { UserService } from '@account-users/user/services/user.service';
import { zip } from 'rxjs';
import { LicenseType } from '@account-users/account/models/LicenseType';

@Injectable()
export class AccountUserEffects {
  @Effect()
  loadAccountUser$ = this.actions$.pipe(
    ofType(
      AccountUserActionTypes.LoadAccountUser,
      AccountUserActionTypes.UpdateAccountUserSectionPermissionsSuccess,
    ),
    switchMap(({ payload }: ILoadAccountUserPayload) => {
      return this.userService
        .getUserDetails(payload.accountId, payload.userId)
        .pipe(
          map((response: IAccountUser) => {
            return new LoadAccountUserSuccess(response);
          }),
          catchError((error) => {
            this.toastr.error('Could not fetch account user', 'Request failed');
            return [new LoadAccountUserFailure()];
          }),
        );
    }),
  );

  @Effect()
  loadAccountUserAfterRoleUpdate$ = this.actions$.pipe(
    ofType(AccountUserActionTypes.UpdateAccountUserRolesSuccess),
    delay(500),
    switchMap(
      ({
        payload: { userId, accountId },
      }: UpdateAccountUserRolesActionSuccess) => {
        return zip(
          this.userService.getUserDetails(accountId, userId),
          this.accountService.getLicensePermissionsForAccountUser(
            accountId,
            userId,
          ),
        ).pipe(
          map(([user, userLicensePermissions]) => {
            return new LoadAccountUserAfterRoleSuccess({
              user,
              userLicensePermissions,
            });
          }),
          catchError((error) => {
            this.toastr.error('Could not fetch account user', 'Request failed');
            return [new LoadAccountUserAfterRoleFailure()];
          }),
        );
      },
    ),
  );

  @Effect()
  loadAccountUserLicensesAndPermissions$ = this.actions$.pipe(
    ofType(AccountUserActionTypes.LoadAllAccountUserLicenses),
    switchMap(({ payload }: LoadAllAccountUserLicensesAction) => {
      return zip(
        this.userService.getUserDetails(payload.accountId, payload.userId),
        this.accountService.getLicensePermissionsForAccountUser(
          payload.accountId,
          payload.userId,
        ),
        this.accountService.getAllLicenses(),
        this.accountService.getLicensePermissions([
          LicenseType.Core,
          LicenseType.Tool,
        ]),
        this.accountService.getAccountLicenses(payload.accountId),
        this.userService.fetchUserRoles(),
        this.accountService.getAccountUsers(payload.accountId),
        this.accountService.getLicenseTypes(),
      ).pipe(
        map(
          ([
            accountUser,
            userLicenses,
            allLicenses,
            licensePermissions,
            accountLicenses,
            userRoles,
            accountUsers,
            licenseTypes,
          ]) => {
            return new LoadAllAccountUserLicensesActionSuccess({
              accountUser,
              userLicenses,
              allLicenses,
              licensePermissions,
              accountLicenses,
              userRoles,
              accountUsers,
              licenseTypes,
            });
          },
        ),
        catchError((error) => {
          this.toastr.error(
            'Could not fetch license permissions for user',
            'Request failed',
          );
          return [new LoadAllAccountUserLicensesActionFailure()];
        }),
      );
    }),
  );

  @Effect()
  loadAccountUserLicensePermissions$ = this.actions$.pipe(
    ofType(
      AccountUserActionTypes.LoadAccountUserPermissions,
      AccountUserActionTypes.UpdateAccountUserLicensePermissionsSuccess,
    ),
    switchMap(({ payload }: ILoadAccountUserPayload) => {
      return this.accountService
        .getLicensePermissionsForAccountUser(payload.accountId, payload.userId)
        .pipe(
          map((response) => {
            return new LoadAccountUserPermissionsSuccess(response);
          }),
          catchError((error) => {
            this.toastr.error(
              'Could not fetch license permissions for user',
              'Request failed',
            );
            return [new LoadAccountUserPermissionsFailure()];
          }),
        );
    }),
  );

  @Effect()
  updateAccountUserLicensePermissions$ = this.actions$.pipe(
    ofType(AccountUserActionTypes.UpdateAccountUserLicensePermissions),
    switchMap(({ payload }: IUpdateLicensePayload) => {
      return this.userService.updatePermission(payload).pipe(
        map((response) => {
          this.toastr.info(`Successfully updated permission!`);
          return new UpdateAccountUserLicensePermissionsSuccess({
            accountId: payload.accountId,
            userId: payload.userId,
          });
        }),
        catchError((error) => {
          this.toastr.error(
            'Could not update license permission',
            'Request failed',
          );
          return [new UpdateAccountUserLicensePermissionFailure()];
        }),
      );
    }),
  );

  @Effect()
  updateAccountUserSectionPermissions$ = this.actions$.pipe(
    ofType(AccountUserActionTypes.UpdateAccountUserSectionPermissions),
    switchMap(({ payload }: IUpdateSectionPayload) => {
      return this.userService.setSectionPermissions(payload).pipe(
        map((response) => {
          this.toastr.info(`Successfully updated permission!`);
          return new UpdateAccountUserSectionPermissionsSuccess({
            accountId: payload.accountId,
            userId: payload.userId,
          });
        }),
        catchError((error) => {
          this.toastr.error(
            'Could not update section permission',
            'Request failed',
          );
          return [new UpdateAccountUserSectionPermissionsFailure()];
        }),
      );
    }),
  );

  @Effect()
  updateAccountUserRoles$ = this.actions$.pipe(
    ofType(AccountUserActionTypes.UpdateAccountUserRoles),
    switchMap(
      ({
        payload: { role, userId, accountId, toggle },
      }: UpdateAccountUserRolesAction) => {
        const method = toggle ? 'put' : 'delete';
        return this.userService
          .updateAccountUserRole(role, userId, accountId, method)
          .pipe(
            map((response) => {
              this.toastr.info(`Successfully updated role!`);
              return new UpdateAccountUserRolesActionSuccess({
                userId,
                accountId,
                role,
                toggle,
              });
            }),
            catchError((error) => {
              this.toastr.error('Could not update role', 'Request failed');
              return [new UpdateAccountUserRolesActionFailure()];
            }),
          );
      },
    ),
  );

  constructor(
    private toastr: ToastrService,
    private actions$: Actions,
    private accountService: AccountService,
    private userService: UserService,
  ) {}
}
